/**
 * Utilitaires pour l'affichage des macros
 *
 * IMPORTANT: Les calculs réels (BMR, DEJ, macros) sont maintenant côté API.
 * Ce fichier ne contient que les fonctions d'affichage/labels.
 *
 * Pour les calculs, utilisez: services/nutritionService.js
 * - nutritionService.calculateBmrDej(clientId)
 * - nutritionService.calculateMacros(clientId, dayType)
 * - nutritionService.calculateAllMacros(clientId)
 */

/**
 * Labels pour les types de jour
 */
export const getDayTypeLabel = (dayType) => {
  const labels = {
    OFF: {
      label: 'Jour OFF',
      description: 'Léger déficit naturel',
      benefits: ['Perte de gras progressive', 'Masse musculaire protégée', 'Récupération optimale'],
      color: 'default',
    },
    ON: {
      label: 'Jour ON',
      description: 'Équilibre énergétique',
      benefits: ['Performance stable', 'Moins de fatigue', 'Pas d\'explosion calorique'],
      color: 'primary',
    },
    INTENSIF: {
      label: 'Jour INTENSIF',
      description: 'Carburant élevé',
      benefits: ['Réserve glycogène', 'Performance maximisée', 'Digestion respectée'],
      color: 'error',
    },
  };

  return labels[dayType] || labels.ON;
};

/**
 * Labels pour les niveaux d'activité (NAP)
 */
export const getNAPLabel = (activityLevel) => {
  const labels = {
    sedentary: { label: 'Sédentaire', nap: 1.4 },
    lightly_active: { label: 'Légèrement actif', nap: 1.5 },
    moderate: { label: 'Modérément actif', nap: 1.6 },
    active: { label: 'Actif', nap: 1.7 },
    very_active: { label: 'Très actif', nap: 1.8 },
  };

  return labels[activityLevel] || labels.moderate;
};

/**
 * Labels pour les périodes sportives
 */
export const getPeriodLabel = (period) => {
  const labels = {
    base: 'Préparation',
    intensification: 'Intensification',
    competition: 'Compétition',
  };

  return labels[period] || labels.base;
};

/**
 * Labels pour les objectifs
 */
export const getGoalTypeLabel = (goalType) => {
  const labels = {
    weight_loss: 'Perte de poids',
    weight_gain: 'Prise de poids',
    maintenance: 'Maintien',
    muscle_gain: 'Prise de muscle',
    health: 'Santé générale',
    performance: 'Performance',
    other: 'Autre',
  };

  return labels[goalType] || goalType;
};

/**
 * Couleur badge selon le type de jour
 */
export const getDayTypeColor = (dayType) => {
  const colors = {
    OFF: 'default',
    ON: 'primary',
    INTENSIF: 'error',
  };

  return colors[dayType] || 'default';
};

/**
 * @deprecated Utilisez nutritionService.calculateBmrDej() à la place
 * Conservé temporairement pour compatibilité
 */
export const calculateBMR = (user) => {
  console.warn('calculateBMR est déprécié. Utilisez nutritionService.calculateBmrDej() à la place.');
  const { age, weight, height, gender } = user;

  if (!age || !weight || !height) return null;

  let bmr;
  if (gender === 'male') {
    bmr = 10 * weight + 6.25 * height - 5 * age + 5;
  } else if (gender === 'female') {
    bmr = 10 * weight + 6.25 * height - 5 * age - 161;
  } else {
    const maleBMR = 10 * weight + 6.25 * height - 5 * age + 5;
    const femaleBMR = 10 * weight + 6.25 * height - 5 * age - 161;
    bmr = (maleBMR + femaleBMR) / 2;
  }

  return Math.round(bmr);
};

/**
 * @deprecated Utilisez nutritionService.calculateBmrDej() à la place
 * Conservé temporairement pour compatibilité
 */
export const calculateDEJ = (bmr, nap) => {
  console.warn('calculateDEJ est déprécié. Utilisez nutritionService.calculateBmrDej() à la place.');
  return Math.round(bmr * nap);
};

export default {
  getDayTypeLabel,
  getNAPLabel,
  getPeriodLabel,
  getGoalTypeLabel,
  getDayTypeColor,
  calculateBMR,
  calculateDEJ,
};
